import {
  AudioWaveform,
  Check,
  CheckCircle2,
  Clock3,
  Copy,
  ExternalLink,
  Gamepad2,
  KeyRound,
  Link2,
  LoaderCircle,
  MonitorSmartphone,
  RefreshCw,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, apiRequest } from "../lib/apiClient";
import { Brand } from "../components/Brand";

type PairingAttempt = {
  attemptId: string;
  code: string;
  browserToken: string;
  expiresAt: number;
};

type GroupPairingAttempt = {
  attemptId: string;
  browserToken: string;
  expiresAt: number;
};

export function PairingPage({ onPaired }: { onPaired: () => void }) {
  const [pairingMethod, setPairingMethod] = useState<"code" | "group">("code");
  const [attempt, setAttempt] = useState<PairingAttempt | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [status, setStatus] = useState<"creating" | "pending" | "paired" | "error">(
    "creating",
  );
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [groupUsername, setGroupUsername] = useState("");
  const [groupAttempt, setGroupAttempt] =
    useState<GroupPairingAttempt | null>(null);
  const [groupStatus, setGroupStatus] = useState<
    "idle" | "creating" | "pending" | "confirmation" | "paired" | "error"
  >("idle");
  const [groupError, setGroupError] = useState("");
  const browserTokenRef = useRef("");
  const groupBrowserTokenRef = useRef("");
  const initialLifetimeRef = useRef(1);
  const copiedTimeoutRef = useRef<number | null>(null);

  const createAttempt = useCallback(async () => {
    setStatus("creating");
    setError("");
    setCopied(false);
    try {
      const created = await apiRequest<PairingAttempt>("/api/v1/pairing/attempts", {
        method: "POST",
      });
      browserTokenRef.current = created.browserToken;
      initialLifetimeRef.current = Math.max(
        1,
        Math.ceil((created.expiresAt - Date.now()) / 1000),
      );
      setAttempt(created);
      setStatus("pending");
    } catch (caught) {
      setStatus("error");
      setError(
        caught instanceof Error
          ? caught.message
          : "Impossible de creer un code d'appairage.",
      );
    }
  }, []);

  useEffect(() => {
    void createAttempt();
  }, [createAttempt]);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current !== null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!attempt || status !== "pending") return;
    let stopped = false;

    const updateRemaining = () => {
      setSecondsRemaining(
        Math.max(0, Math.ceil((attempt.expiresAt - Date.now()) / 1000)),
      );
    };
    updateRemaining();
    const countdown = window.setInterval(updateRemaining, 250);

    const poll = window.setInterval(async () => {
      try {
        const result = await apiRequest<{
          status: "pending" | "paired";
          expiresAt: number;
        }>(`/api/v1/pairing/attempts/${attempt.attemptId}/status`, {
          headers: { Authorization: `Bearer ${browserTokenRef.current}` },
        });
        if (stopped || result.status !== "paired") return;
        setStatus("paired");
        window.setTimeout(onPaired, 450);
      } catch (caught) {
        if (stopped) return;
        if (
          caught instanceof ApiError &&
          ["pairing_expired", "pairing_consumed"].includes(caught.code)
        ) {
          setStatus("error");
          setError("Ce code a expire. Generez-en un nouveau.");
        }
      }
    }, 1_500);

    return () => {
      stopped = true;
      window.clearInterval(countdown);
      window.clearInterval(poll);
    };
  }, [attempt, onPaired, status]);

  useEffect(() => {
    if (
      !groupAttempt ||
      !["pending", "confirmation"].includes(groupStatus)
    ) {
      return;
    }
    let stopped = false;
    const poll = window.setInterval(async () => {
      try {
        const result = await apiRequest<{
          status: "pending" | "confirmation" | "paired";
          expiresAt: number;
        }>(
          `/api/v1/pairing/group-attempts/${groupAttempt.attemptId}/status`,
          {
            headers: {
              Authorization: `Bearer ${groupBrowserTokenRef.current}`,
            },
          },
        );
        if (stopped) return;
        if (result.status === "paired") {
          setGroupStatus("paired");
          window.setTimeout(onPaired, 450);
          return;
        }
        setGroupStatus(result.status);
      } catch (caught) {
        if (stopped) return;
        if (
          caught instanceof ApiError &&
          [
            "group_pairing_expired",
            "group_pairing_rejected",
            "group_pairing_not_found",
          ].includes(caught.code)
        ) {
          setGroupStatus("error");
          setGroupError(
            caught.code === "group_pairing_rejected"
              ? "La demande a ete refusee dans Roblox."
              : "La demande a expire. Relancez la connexion.",
          );
        }
      }
    }, 1_500);
    return () => {
      stopped = true;
      window.clearInterval(poll);
    };
  }, [groupAttempt, groupStatus, onPaired]);

  const formattedTime = `${Math.floor(secondsRemaining / 60)}:${String(
    secondsRemaining % 60,
  ).padStart(2, "0")}`;
  const expiryProgress = Math.max(
    0,
    Math.min(100, (secondsRemaining / initialLifetimeRef.current) * 100),
  );

  async function copyCode() {
    if (!attempt) return;
    await navigator.clipboard.writeText(attempt.code);
    setCopied(true);
    if (copiedTimeoutRef.current !== null) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 1_800);
  }

  async function startGroupPairing(event: React.FormEvent) {
    event.preventDefault();
    const username = groupUsername.trim();
    if (!/^[A-Za-z0-9_]{3,20}$/.test(username)) {
      setGroupStatus("error");
      setGroupError("Saisissez votre nom d'utilisateur Roblox exact.");
      return;
    }
    setGroupStatus("creating");
    setGroupError("");
    try {
      const created = await apiRequest<GroupPairingAttempt>(
        "/api/v1/pairing/group-attempts",
        {
          method: "POST",
          body: { username },
        },
      );
      groupBrowserTokenRef.current = created.browserToken;
      setGroupAttempt(created);
      setGroupStatus("pending");
    } catch (caught) {
      setGroupStatus("error");
      setGroupError(
        caught instanceof Error
          ? caught.message
          : "Impossible de demarrer la liaison groupe.",
      );
    }
  }

  return (
    <div className="pairing-page">
      <header className="public-header">
        <Brand />
        <div className="public-header-actions">
          <span className="service-availability">
            <i />
            Service disponible
          </span>
          <nav aria-label="Liens secondaires">
            <a href="/customer">Espace studio</a>
            <a href="/legal/privacy">Confidentialite</a>
          </nav>
        </div>
      </header>

      <main className="pairing-main">
        <section className="pairing-copy">
          <div className="pairing-signal" aria-hidden="true">
            <span><AudioWaveform size={16} /> Canal vocal securise</span>
            <div>
              {Array.from({ length: 12 }, (_, index) => <i key={index} />)}
            </div>
          </div>
          <p className="eyebrow">Compagnon vocal spatial</p>
          <h1>Reliez votre session Roblox.</h1>
          <p className="pairing-lead">
            Vox associe ce navigateur au joueur present dans votre serveur.
            Chaque demande est temporaire et doit etre validee dans Roblox.
          </p>
          <div className="pairing-route" aria-label="Liaison du navigateur a Roblox">
            <span>
              <MonitorSmartphone size={18} />
              <small>Ce navigateur</small>
              <strong>Microphone</strong>
            </span>
            <i aria-hidden="true" />
            <span>
              <Gamepad2 size={18} />
              <small>Votre session</small>
              <strong>Roblox</strong>
            </span>
          </div>
          <div className="trust-row">
            <span><ShieldCheck size={16} /> Validation a usage unique</span>
            <span><UsersRound size={16} /> Acces groupe controle en jeu</span>
          </div>
        </section>

        <section className="pairing-panel" aria-live="polite">
          <div className="pairing-panel-header">
            <span className="step-index">01</span>
            <div>
              <p className="eyebrow">Connexion a votre joueur</p>
              <h2>
                {pairingMethod === "code"
                  ? "Utilisez ce code dans Roblox"
                  : "Connexion par groupe Roblox"}
              </h2>
            </div>
            <span className="pairing-channel">Canal prive</span>
          </div>

          <div className="pairing-method-switch" role="tablist">
            <button
              className={pairingMethod === "code" ? "is-active" : ""}
              role="tab"
              aria-selected={pairingMethod === "code"}
              onClick={() => setPairingMethod("code")}
            >
              <KeyRound size={15} /> Code
            </button>
            <button
              className={pairingMethod === "group" ? "is-active" : ""}
              role="tab"
              aria-selected={pairingMethod === "group"}
              onClick={() => setPairingMethod("group")}
            >
              <UsersRound size={15} /> Groupe
            </button>
          </div>

          {pairingMethod === "code" ? (
            <>
              {status === "creating" ? (
                <div className="pairing-loading">
                  <LoaderCircle className="spin" />
                  Creation d'un code securise...
                </div>
              ) : null}

              {attempt && status !== "creating" ? (
                <>
                  <div className="pairing-code-row">
                    <strong className="pairing-code">{attempt.code}</strong>
                    <button
                      className="secondary-button icon-label-button"
                      type="button"
                      aria-label="Copier le code d'appairage"
                      onClick={() => void copyCode()}
                    >
                      {copied ? <Check size={17} /> : <Copy size={17} />}
                      {copied ? "Copie" : "Copier"}
                    </button>
                  </div>
                  <div className="pairing-expiry">
                    <div>
                      <span>Temps restant</span>
                      <strong>{formattedTime}</strong>
                    </div>
                    <span className="pairing-expiry-track" aria-hidden="true">
                      <i style={{ width: `${expiryProgress}%` }} />
                    </span>
                  </div>
                </>
              ) : null}

              <ol className="pairing-steps">
                <li>
                  <span>1</span>
                  <div>
                    <strong>Ouvrez Vox dans Roblox</strong>
                    <small>Depuis votre partie, affichez le panneau d'appairage.</small>
                  </div>
                  <kbd>F8</kbd>
                </li>
                <li>
                  <span>2</span>
                  <div>
                    <strong>Saisissez le code</strong>
                    <small>Le serveur verifie directement votre identite Roblox.</small>
                  </div>
                </li>
                <li>
                  <span>3</span>
                  <div>
                    <strong>Autorisez le microphone</strong>
                    <small>Cette page ouvrira la salle vocale automatiquement.</small>
                  </div>
                </li>
              </ol>

              <div className={`pairing-state pairing-state-${status}`}>
                {status === "pending" ? (
                  <>
                    <span className="pulse-dot" />
                    En attente de Roblox
                  </>
                ) : null}
                {status === "paired" ? (
                  <>
                    <CheckCircle2 size={17} />
                    Identite verifiee. Ouverture de la salle...
                  </>
                ) : null}
                {status === "error" ? (
                  <>
                    <span>{error}</span>
                    <button className="text-button" onClick={() => void createAttempt()}>
                      <RefreshCw size={15} /> Nouveau code
                    </button>
                  </>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <form
                className="group-pairing-form"
                onSubmit={(event) => void startGroupPairing(event)}
              >
                <label>
                  Nom d'utilisateur Roblox
                  <input
                    autoComplete="username"
                    placeholder="Votre pseudo exact"
                    value={groupUsername}
                    disabled={[
                      "creating",
                      "pending",
                      "confirmation",
                      "paired",
                    ].includes(groupStatus)}
                    onChange={(event) => setGroupUsername(event.target.value)}
                  />
                </label>
                <p>
                  Disponible uniquement si le proprietaire du jeu a lie sa
                  licence Vox a votre groupe.
                </p>
                <button
                  className="primary-button"
                  disabled={
                    !groupUsername.trim() ||
                    ["creating", "pending", "confirmation", "paired"].includes(
                      groupStatus,
                    )
                  }
                  type="submit"
                >
                  {groupStatus === "creating" ? (
                    <LoaderCircle className="spin" size={16} />
                  ) : (
                    <UsersRound size={16} />
                  )}
                  Demander la connexion
                </button>
              </form>

              <ol className="pairing-steps group-pairing-steps">
                <li>
                  <span>1</span>
                  <div>
                    <strong>Restez dans votre partie</strong>
                    <small>Le serveur controle votre groupe et votre rang.</small>
                  </div>
                </li>
                <li>
                  <span>2</span>
                  <div>
                    <strong>Confirmez dans Roblox</strong>
                    <small>Une demande Vox apparaitra uniquement pour votre joueur.</small>
                  </div>
                </li>
              </ol>

              <div
                className={`pairing-state pairing-state-${
                  groupStatus === "error" ? "error" : groupStatus
                }`}
              >
                {groupStatus === "idle" ? (
                  <>Aucun code a saisir.</>
                ) : null}
                {groupStatus === "creating" || groupStatus === "pending" ? (
                  <>
                    <span className="pulse-dot" />
                    Recherche de votre session Roblox
                  </>
                ) : null}
                {groupStatus === "confirmation" ? (
                  <>
                    <span className="pulse-dot" />
                    Confirmez maintenant dans Roblox
                  </>
                ) : null}
                {groupStatus === "paired" ? (
                  <>
                    <CheckCircle2 size={17} />
                    Identite verifiee. Ouverture de la salle...
                  </>
                ) : null}
                {groupStatus === "error" ? (
                  <>
                    <span>{groupError}</span>
                    <button
                      className="text-button"
                      onClick={() => {
                        setGroupAttempt(null);
                        setGroupStatus("idle");
                        setGroupError("");
                      }}
                    >
                      <RefreshCw size={15} /> Reessayer
                    </button>
                  </>
                ) : null}
              </div>
            </>
          )}
        </section>

        <aside className="pairing-notice">
          <Link2 size={18} />
          <div>
            <strong>Service tiers independant</strong>
            <p>
              Vox utilise votre microphone dans ce navigateur apres votre accord.
              Aucun audio n'est enregistre par defaut.
            </p>
            <a href="/legal/privacy">
              Consulter le traitement des donnees <ExternalLink size={13} />
            </a>
          </div>
        </aside>
      </main>

      <footer className="public-footer">
        <span>Vox est un service independant, non affilie a Roblox Corporation.</span>
        <div>
          <a href="/legal/terms">Conditions</a>
          <a href="/legal/privacy">Confidentialite</a>
          <a href="/legal/moderation">Moderation</a>
        </div>
      </footer>
    </div>
  );
}
